/**
 * Shared artifact engine: manages ArtifactStore + WebSocket connection.
 * Used by both the desktop ArtifactPanel and the MobileArtifactSheet.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSyncExternalStore } from 'react';
import { toast } from 'sonner';

import type {
  A2UIMessage,
  AgentCanvasSubmission,
  AgentCanvasChatContext,
  ArtifactExportResponse,
  ValueMsg,
} from '@/artifact/types';
import {
  ArtifactStore,
  componentMsgToNode,
  computeRootCuids,
  unwrapDataModel,
  type ArtifactCallbacks,
} from '@/artifact/store';
import { RenderNode } from '@/artifact/RenderNode';
import { exportSurfaceSnapshot, waitForCaptureTarget, withExportTimeout } from '@/artifact/export';
import { downloadUrl } from '@/lib/utils';
import { useOptionalVmClient } from '@/api/context';

/** Map frontend event names to Python callback names.
 * NOTE: duplicated in DevArtifactApp.tsx — unify these at some future point. */
const EVENT_TO_CALLBACK: Record<string, string> = {
  plotly_click: 'on_trace_click',
  plotly_canvas_click: 'on_canvas_click',
  plotly_selected: 'on_trace_select',
  three_click: 'on_object_click',
  svg_click: 'on_shape_click',
};

export interface ArtifactEngine {
  store: ArtifactStore;
  connected: boolean;
  pages: ReturnType<ArtifactStore['getState']>['pages'];
  viewingPageIndex: number | null;
  effectiveComponents: Map<number, import('@/artifact/types').ComponentNode>;
  theme: ReturnType<ArtifactStore['getState']>['theme'];
  liveComponentsSize: number;
  rootCuids: number[];
  focusTarget: ArtifactFocusTarget | null;
  hasComponents: boolean;
  hasPages: boolean;
  totalPages: number;
  callbacks: ArtifactCallbacks;
  /**
   * Components should call this to register a function that returns the
   * visible `.a-surface` element. Used for server-initiated export requests.
   */
  setSurfaceGetter: (fn: (() => HTMLElement | null) | null) => void;
  /** Delete an archived page by index (0-based into pages[]). */
  deletePage: (index: number) => Promise<void>;
  /** Clear the live page without touching archived pages. */
  clearCurrentPage: () => Promise<void>;
  /** Send a visual annotation of the artifact panel back to the agent. */
  sendAgentCanvasSubmission: (submission: AgentCanvasSubmission) => boolean;
  setAgentCanvasController: (controller: AgentCanvasController | null) => void;
  collectAgentCanvasContext: () => Promise<AgentCanvasChatContext | null>;
  clearAgentCanvasContext: () => void;
}

export interface AgentCanvasController {
  collectContext: () => Promise<AgentCanvasChatContext | null>;
  clear: () => void;
}

export interface ArtifactFocusTarget {
  cuid: number;
  nonce: number;
}

export function useArtifactEngine(
  sessionId: string | null | undefined,
  onActivate?: () => void,
  agentStatus?: string,
): ArtifactEngine {
  const agentStatusRef = useRef(agentStatus);
  agentStatusRef.current = agentStatus;
  const apiClient = useOptionalVmClient();
  const storeRef = useRef<ArtifactStore | null>(null);
  if (!storeRef.current) storeRef.current = new ArtifactStore();
  const store = storeRef.current;

  const connected = useSyncExternalStore(store.subscribe, () => store.getState().connected);
  const pages = useSyncExternalStore(store.subscribe, () => store.getState().pages);
  const viewingPageIndex = useSyncExternalStore(store.subscribe, () => store.getState().viewingPageIndex);
  const effectiveRoots = useSyncExternalStore(store.subscribe, store.getEffectiveRoots);
  const effectiveComponents = useSyncExternalStore(store.subscribe, store.getEffectiveComponents);
  const theme = useSyncExternalStore(store.subscribe, () => store.getState().theme);
  const liveComponentsSize = useSyncExternalStore(store.subscribe, () => store.getState().components.size);
  const [focusTarget, setFocusTarget] = useState<ArtifactFocusTarget | null>(null);
  const focusNonceRef = useRef(0);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const connectionGenRef = useRef(0);

  const onActivateRef = useRef(onActivate);
  onActivateRef.current = onActivate;

  const surfaceGetterRef = useRef<(() => HTMLElement | null) | null>(null);
  const agentCanvasControllerRef = useRef<AgentCanvasController | null>(null);

  const setSurfaceGetter = useCallback((fn: (() => HTMLElement | null) | null) => {
    surfaceGetterRef.current = fn;
  }, []);

  const setAgentCanvasController = useCallback((controller: AgentCanvasController | null) => {
    agentCanvasControllerRef.current = controller;
  }, []);

  const collectAgentCanvasContext = useCallback(async () => (
    await agentCanvasControllerRef.current?.collectContext() ?? null
  ), []);

  const clearAgentCanvasContext = useCallback(() => {
    agentCanvasControllerRef.current?.clear();
  }, []);

  const deletePage = useCallback(
    async (index: number) => {
      store.dispatch({ type: 'DELETE_PAGE', index });
      if (sessionId && apiClient) {
        try {
          await apiClient.deleteArtifactPage(sessionId, index);
        } catch {
          // Best-effort: the UI already removed the page locally
        }
      }
    },
    [store, sessionId, apiClient],
  );

  const clearCurrentPage = useCallback(
    async () => {
      store.dispatch({ type: 'BEGIN_RENDERING', theme: {} });
      if (sessionId && apiClient) {
        try {
          await apiClient.deleteArtifactPage(sessionId, store.getState().pages.length);
        } catch {
          // Best-effort: the UI already cleared the live page locally
        }
      }
    },
    [store, sessionId, apiClient],
  );

  const sendAgentCanvasSubmission = useCallback(
    (submission: AgentCanvasSubmission): boolean => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        toast.error('Agent Canvas is not connected.');
        return false;
      }
      if (agentStatusRef.current === 'off') {
        toast.warning('Agent is powered off.', {
          id: 'agent-powered-off',
          description: 'Click Start to wake it up.',
        });
        return false;
      }
      ws.send(JSON.stringify(submission));
      return true;
    },
    [],
  );

  const sendEvent = useCallback(
    (comp_id: string, event: string, data: unknown) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      // Server still accepts the message but no worker is alive to handle
      // it — surface a toast so the user knows why their click did nothing.
      if (agentStatusRef.current === 'off') {
        toast.warning('Agent is powered off.', {
          id: 'agent-powered-off',
          description: 'Click Start to wake it up.',
        });
        return;
      }

      // Try to find a callback binding for this event.
      // Components store callbacks as {on_click: 3, on_change: 5, ...}
      const cuid = Number(comp_id);
      const node = store.getEffectiveComponents().get(cuid);
      const cbName = EVENT_TO_CALLBACK[event] ?? `on_${event}`;
      const cbId = node?.callbacks[cbName];

      const eventData = typeof data === 'object' && data !== null ? data : {};
      const eventObj = { uid: cuid, ...eventData };

      if (cbId != null) {
        ws.send(JSON.stringify({
          type: 'eventCallback',
          binding: { comp_id: cuid, cb_name: cbName, cb_id: cbId },
          event: eventObj,
        }));
      } else {
        ws.send(JSON.stringify({
          type: 'userEvent',
          comp_id: cuid,
          event: eventObj,
        }));
      }
    },
    [store],
  );

  const renderNode = useCallback(
    (nodeId: string): ReactNode => {
      return <RenderNode key={nodeId} cuid={Number(nodeId)} />;
    },
    [],
  );

  const callbacks = useMemo(
    () => ({ sendEvent, renderNode }),
    [sendEvent, renderNode],
  );

  useEffect(() => {
    connectionGenRef.current += 1;
    const generation = connectionGenRef.current;
    store.dispatch({ type: 'RESET' });

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = undefined;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (!sessionId) {
      store.dispatch({ type: 'SET_CONNECTED', connected: false });
      return;
    }

    function connect() {
      if (generation !== connectionGenRef.current) return;
      if (!apiClient) return;
      const wsUrl = apiClient.getArtifactWebSocketUrl(sessionId!);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (generation !== connectionGenRef.current) return;
        store.dispatch({ type: 'RESET' });
        store.dispatch({ type: 'SET_CONNECTED', connected: true });
      };

      ws.onclose = () => {
        if (generation !== connectionGenRef.current) return;
        store.dispatch({ type: 'SET_CONNECTED', connected: false });
        wsRef.current = null;
        reconnectTimerRef.current = setTimeout(() => {
          if (generation === connectionGenRef.current && sessionId) connect();
        }, 2000);
      };

      ws.onerror = () => {
        if (generation !== connectionGenRef.current) return;
        ws.close();
      };

      ws.onmessage = (event) => {
        if (generation !== connectionGenRef.current) return;
        try {
          const msg: A2UIMessage = JSON.parse(event.data);
          if (msg.type === 'artifactExportRequest') {
            void handleExportRequest(msg, ws);
            return;
          }
          if (msg.type === 'userDownload') {
            void handleUserDownload(msg);
            return;
          }
          handleMessage(msg);
        } catch {
          // ignore
        }
      };
    }

    async function handleExportRequest(
      msg: Extract<A2UIMessage, { type: 'artifactExportRequest' }>,
      ws: WebSocket,
    ) {
      const response: ArtifactExportResponse = {
        type: 'artifactExportResponse',
        requestId: msg.requestId,
        format: msg.format,
      };

      const target = await waitForCaptureTarget(
        () => surfaceGetterRef.current?.() ?? null,
        msg.componentId,
      );
      if (!target.ok) {
        response.error = target.error;
        ws.send(JSON.stringify(response));
        return;
      }

      try {
        const payload = await withExportTimeout(
          exportSurfaceSnapshot(
            target.target,
            msg.format,
            undefined,
            msg.maxHeight,
            msg.offsetY,
            msg.format === 'png' ? { imageMimeType: 'image/webp' } : undefined,
          ),
          msg.timeoutMs,
          `Artifact ${msg.format} export`,
        );
        response.mimeType = payload.mimeType;
        response.data = payload.base64;
      } catch (error) {
        response.error = error instanceof Error ? error.message : String(error);
      }

      ws.send(JSON.stringify(response));
    }

    async function handleUserDownload(
      msg: Extract<A2UIMessage, { type: 'userDownload' }>,
    ) {
      if (!apiClient) return;

      try {
        await downloadUrl(apiClient.getDownloadUrl(msg.downloadId), msg.filename);
        toast.success(`${msg.label ?? msg.filename} downloaded`);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        toast.error(`Download failed: ${message}`);
      }
    }

    function handleMessage(msg: A2UIMessage) {
      switch (msg.type) {
        case 'beginRendering':
          store.dispatch({ type: 'BEGIN_RENDERING', theme: msg.theme ?? {} });
          break;
        case 'surfaceUpdate': {
          const nodes = (msg.components ?? []).map(componentMsgToNode);
          const existingComponents = store.getState().components;
          const addedNodes = nodes.filter((node) => !existingComponents.has(node.cuid));
          if (nodes.length > 0) {
            onActivateRef.current?.();
          }
          store.dispatch({ type: 'SURFACE_UPDATE', roots: msg.roots ?? {}, components: nodes });
          const focusNode = addedNodes.at(-1);
          if (focusNode) {
            focusNonceRef.current += 1;
            setFocusTarget({ cuid: focusNode.cuid, nonce: focusNonceRef.current });
          }
          break;
        }
        case 'dataModelUpdate': {
          const data = unwrapDataModel((msg.data ?? {}) as Record<string, ValueMsg>);
          store.dispatch({ type: 'DATA_MODEL_UPDATE', data });
          break;
        }
        case 'updateCallbacks':
          store.dispatch({ type: 'UPDATE_CALLBACKS', bindings: msg.bindings ?? [] });
          break;
        case 'deleteSurface':
          store.dispatch({ type: 'DELETE_SURFACE', comp_ids: msg.comp_ids });
          break;
        case 'newPage':
          store.dispatch({ type: 'NEW_PAGE', label: msg.label ?? 'Page' });
          break;
        case 'showNotification': {
          const duration = (msg.duration ?? 2) * 1000;
          const style = msg.style ?? 'info';
          if (style === 'error') toast.error(msg.text, { duration });
          else if (style === 'warning') toast.warning(msg.text, { duration });
          else if (style === 'debug') toast(msg.text, { duration, className: 'a-toast-debug' });
          else if (style === 'muted') toast(msg.text, { duration, className: 'a-toast-muted' });
          else toast.info(msg.text, { duration });
          break;
        }
      }
    }

    connect();

    return () => {
      connectionGenRef.current += 1;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = undefined;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      store.dispatch({ type: 'RESET' });
    };
  }, [sessionId, store, apiClient]);

  const rootCuids = useMemo(
    () => computeRootCuids(effectiveRoots),
    [effectiveRoots],
  );

  const hasComponents = rootCuids.length > 0;
  const hasPages = pages.length > 0;
  const totalPages = pages.length + 1;

  return {
    store,
    connected,
    pages,
    viewingPageIndex,
    effectiveComponents,
    theme,
    liveComponentsSize,
    rootCuids,
    focusTarget,
    hasComponents,
    hasPages,
    totalPages,
    callbacks,
    setSurfaceGetter,
    deletePage,
    clearCurrentPage,
    sendAgentCanvasSubmission,
    setAgentCanvasController,
    collectAgentCanvasContext,
    clearAgentCanvasContext,
  };
}
